import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  useToast,
} from '@shared/ui';
import type { AssetBalanceDto, AutoWithdrawalDto, AutoWithdrawalRoutingMode } from '@shared/api/contracts';
import {
  areAutoWithdrawalAddressesEqual,
  hasRoutingModeChanged,
  mapEnabledDraftsToPayload,
  mapSettingsToDrafts,
  normalizeAutoWithdrawalDto,
  prefillDirectFromSingle,
  prefillSingleFromDirect,
  reorderEnabledDirectDrafts,
  type AutoWithdrawalDrafts,
} from '@entities/withdrawal';
import { getFirstInvalidRuleKey, validateAutoWithdrawalDrafts } from '../../lib/validation';
import { useAutoWithdrawal } from '../../use-auto-withdrawal';
import { useSecondFactorAction } from '@features/security';
import { useApiServices } from '@shared/lib/use-api-services';
import { swrKeys } from '@shared/api/swr/keys';
import { useWithdrawalsLocked, WithdrawalsLockBanner } from '../withdrawals-lock-banner';
import { ModeSelector } from './mode-selector';
import { SingleRuleForm } from './single-rule-form';
import { DirectRulesList } from './direct-rules-list';

interface AutoWithdrawalModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  autoWithdraw: AutoWithdrawalDto | null;
  assetBalances: AssetBalanceDto[];
  totalBalance?: number;
  availableBalance?: number;
  onSaved?: () => void;
}

export const AutoWithdrawalModal = ({
  open,
  onOpenChange,
  autoWithdraw,
  assetBalances,
  totalBalance,
  availableBalance,
  onSaved,
}: AutoWithdrawalModalProps) => {
  const { t, i18n } = useTranslation('cabinet');
  const services = useApiServices();
  const addresses = useSWR(open ? swrKeys.security.withdrawalAddresses() : null, () =>
    services.security.getWithdrawalAddresses(),
  );
  const { toast } = useToast();
  const { saveAutoWithdrawal, isLoading } = useAutoWithdrawal();
  const { run } = useSecondFactorAction();
  const withdrawalsLocked = useWithdrawalsLocked();

  const normalizedSettings = useMemo(() => normalizeAutoWithdrawalDto(autoWithdraw), [autoWithdraw]);
  const [drafts, setDrafts] = useState<AutoWithdrawalDrafts>(() => mapSettingsToDrafts(normalizedSettings));
  const [formError, setFormError] = useState<string | undefined>();
  const [ruleErrors, setRuleErrors] = useState<Record<string, import('@features/withdrawal/lib/validation').AutoWithdrawalRuleFieldErrors>>({});
  const [isModeConfirmOpen, setIsModeConfirmOpen] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(false);
  const [swapFeeAcknowledged, setSwapFeeAcknowledged] = useState(false);
  const [successCopy, setSuccessCopy] = useState<{ title: string; description: string } | null>(null);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    const justOpened = open && !wasOpenRef.current;
    wasOpenRef.current = open;

    if (!justOpened) {
      return;
    }

    setDrafts(mapSettingsToDrafts(normalizedSettings));
    setFormError(undefined);
    setRuleErrors({});
    setPendingSubmit(false);
    setSwapFeeAcknowledged(Boolean(normalizedSettings?.routingMode === 'SINGLE_TARGET_SWAP'));
  }, [open, normalizedSettings]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (isLoading) return;
    onOpenChange(nextOpen);
  };

  const handleModeChange = (mode: AutoWithdrawalRoutingMode) => {
    if (mode === drafts.routingMode) return;

    setDrafts((prev) => {
      if (mode === 'DIRECT_BY_NETWORK') {
        return {
          routingMode: mode,
          singleDraft: prev.singleDraft,
          directDrafts: prefillDirectFromSingle(prev.singleDraft, prev.directDrafts),
        };
      }

      return {
        routingMode: mode,
        singleDraft: prefillSingleFromDirect(prev.directDrafts, prev.singleDraft),
        directDrafts: prev.directDrafts,
      };
    });
  };

  const submit = async () => {
    if (withdrawalsLocked) {
      return;
    }

    const validation = await validateAutoWithdrawalDrafts(drafts, '', t);
    setFormError(validation.formError);
    setRuleErrors(validation.ruleErrors);

    if (validation.formError || Object.keys(validation.ruleErrors).length > 0) {
      const invalidKey = getFirstInvalidRuleKey(validation.ruleErrors);
      if (invalidKey) {
        document.getElementById(`auto-direct-address-${invalidKey}`)?.focus();
      }
      return;
    }

    const blockedItems = (addresses.data?.items ?? []).filter((item) => item.amlBlocked);
    const draftsToCheck =
      drafts.routingMode === 'DIRECT_BY_NETWORK'
        ? drafts.directDrafts.filter((draft) => draft.enabled)
        : [drafts.singleDraft];
    const usesBlocked = draftsToCheck.some((draft) =>
      blockedItems.some((item) =>
        areAutoWithdrawalAddressesEqual(draft.networkCode, item.address, draft.destinationAddress),
      ),
    );
    if (usesBlocked) {
      setFormError(t('security.addressAmlBlockedHint'));
      return;
    }

    if (hasRoutingModeChanged(drafts, normalizedSettings?.routingMode)) {
      setPendingSubmit(true);
      setIsModeConfirmOpen(true);
      return;
    }

    await performSave();
  };

  const performSave = async () => {
    const outcome = await run({
      purpose: 'auto_withdrawal.change',
      required: true,
      request: (factor) => saveAutoWithdrawal(mapEnabledDraftsToPayload(drafts, factor), normalizedSettings),
    });
    if (!outcome.ok) {
      return;
    }
    const result = outcome.data;

    if (result.success) {
      const pending = result.status === 'pending' || result.status === 'partial';
      setSuccessCopy({
        title: t('withdrawal.auto.modal.success'),
        description: t(
          pending
            ? 'withdrawal.auto.modal.successPendingVerification'
            : 'withdrawal.auto.modal.successDetail',
        ),
      });
      onSaved?.();
      onOpenChange(false);
      return;
    }

    if (result.notFound) {
      toast(t('withdrawal.auto.modal.error'), 'error');
      return;
    }

    if (result.error) {
      toast(result.error, 'error');
    }
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    void submit();
  };

  const requiresSwapAck = drafts.routingMode === 'SINGLE_TARGET_SWAP';

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          className="flex max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] flex-col overflow-hidden sm:max-w-2xl lg:max-w-3xl"
        >
          <DialogHeader>
            <DialogTitle>{t('withdrawal.auto.modal.title')}</DialogTitle>
            <DialogDescription>{t('withdrawal.auto.modal.description')}</DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
            <div
              className="scrollbar-thin modal-scroll-pane min-h-0 flex-1 space-y-4 overflow-y-auto py-px [scrollbar-gutter:stable]"
            >
              <WithdrawalsLockBanner />
              <ModeSelector value={drafts.routingMode} disabled={isLoading} onChange={handleModeChange} />

              {formError && <p className="text-sm text-destructive">{formError}</p>}

              {drafts.routingMode === 'SINGLE_TARGET_SWAP' ? (
                <SingleRuleForm
                  draft={drafts.singleDraft}
                  assetBalances={assetBalances}
                  totalBalance={totalBalance}
                  availableBalance={availableBalance}
                  disabled={isLoading}
                  errors={ruleErrors[drafts.singleDraft.key] ?? ruleErrors['USDT:TRC20']}
                  addresses={addresses.data?.items ?? []}
                  language={i18n.language}
                  onNetworkChange={(networkCode) =>
                    setDrafts((prev) => ({
                      ...prev,
                      singleDraft: { ...prev.singleDraft, networkCode, key: `USDT:${networkCode}` },
                    }))
                  }
                  onAddressChange={(destinationAddress) =>
                    setDrafts((prev) => ({
                      ...prev,
                      singleDraft: { ...prev.singleDraft, destinationAddress },
                    }))
                  }
                  onAmountModeChange={(amountMode) =>
                    setDrafts((prev) => ({
                      ...prev,
                      singleDraft: {
                        ...prev.singleDraft,
                        amountMode,
                        reserveAmount: amountMode === 'FULL_BALANCE' ? '' : prev.singleDraft.reserveAmount,
                      },
                    }))
                  }
                  onThresholdChange={(thresholdAmount) =>
                    setDrafts((prev) => ({
                      ...prev,
                      singleDraft: { ...prev.singleDraft, thresholdAmount },
                    }))
                  }
                  onReserveChange={(reserveAmount) =>
                    setDrafts((prev) => ({
                      ...prev,
                      singleDraft: { ...prev.singleDraft, reserveAmount },
                    }))
                  }
                />
              ) : (
                <DirectRulesList
                  drafts={drafts.directDrafts}
                  assetBalances={assetBalances}
                  disabled={isLoading}
                  ruleErrors={ruleErrors}
                  addresses={addresses.data?.items ?? []}
                  language={i18n.language}
                  firstInvalidKey={getFirstInvalidRuleKey(ruleErrors)}
                  onDraftsChange={(directDrafts) => setDrafts((prev) => ({ ...prev, directDrafts }))}
                  onReorder={(pairKey, direction) =>
                    setDrafts((prev) => ({
                      ...prev,
                      directDrafts: reorderEnabledDirectDrafts(prev.directDrafts, pairKey, direction),
                    }))
                  }
                />
              )}
            </div>

            <div className="mt-4 space-y-4 border-t border-border pt-4">
              {requiresSwapAck && (
                <label className="flex cursor-pointer items-start gap-2.5 text-sm">
                  <Checkbox
                    checked={swapFeeAcknowledged}
                    disabled={isLoading}
                    onCheckedChange={(checked) => setSwapFeeAcknowledged(checked === true)}
                  />
                  <span>{t('withdrawal.auto.modal.swapAck')}</span>
                </label>
              )}
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  disabled={isLoading}
                  onClick={() => handleOpenChange(false)}
                >
                  {t('withdrawal.auto.modal.cancel')}
                </Button>
                <Button
                  type="submit"
                  loading={isLoading}
                  disabled={withdrawalsLocked || (requiresSwapAck && !swapFeeAcknowledged)}
                >
                  {t('withdrawal.auto.modal.button')}
                </Button>
              </DialogFooter>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={isModeConfirmOpen} onOpenChange={setIsModeConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('withdrawal.auto.modeChangeConfirm.title')}</DialogTitle>
            <DialogDescription>{t('withdrawal.auto.modeChangeConfirm.description')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsModeConfirmOpen(false);
                setPendingSubmit(false);
              }}
            >
              {t('withdrawal.auto.modal.cancel')}
            </Button>
            <Button
              type="button"
              loading={isLoading}
              onClick={() => {
                setIsModeConfirmOpen(false);
                if (pendingSubmit) {
                  void performSave();
                }
              }}
            >
              {t('withdrawal.auto.modeChangeConfirm.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={successCopy != null} onOpenChange={(next) => !next && setSuccessCopy(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{successCopy?.title}</DialogTitle>
            <DialogDescription>{successCopy?.description}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" onClick={() => setSuccessCopy(null)}>
              {t('withdrawal.auto.modal.done')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
